import React from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Paper,
  Grid,
  Button,
  Chip,
  DialogActions,
  DialogContentText,
  Typography,
  useMediaQuery,
  useTheme,
  Alert,
} from '@mui/material';
import {
  ActionBar as StandardActionBar,
  SummaryCards as StandardSummaryCards,
  PageHeader,
  RefreshButton,
  StandardTable,
  FilterBar as ModernFilterBar,
  EnhancedTable,
  ActionMenu
} from './StandardComponents';
import TableSkeleton from './TableSkeleton';
import EmptyState from './EmptyState';
import ExportButton from './ExportButton';
import LoadingButton from './LoadingButton';
import FormField from './FormField';

// Action bar - use standardized component
export const ActionBar = StandardActionBar;

// Modern Filter container with basic/advanced toggle
export const FilterBar = (props) => <ModernFilterBar {...props} />;

// Legacy FilterBar for backward compatibility
export const LegacyFilterBar = ({ children, hasActiveFilters = false, onClear, clearLabel = 'Clear', filterChips = null, sx = {} }) => (
  <>
    {/* Active Filter Chips */}
    {hasActiveFilters && filterChips && (
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {filterChips}
      </Box>
    )}

    <Paper sx={{ p: 2, mb: 3, ...sx }}>
      <Grid container spacing={2} alignItems="center">
        {children}
        <Grid item xs={12} md={'auto'}>
          {hasActiveFilters && !filterChips && <Chip label="Filtered" color="primary" size="small" sx={{ mr: 1 }} />}
          {onClear && (
            <Button variant="outlined" onClick={onClear}>{clearLabel}</Button>
          )}
        </Grid>
      </Grid>
    </Paper>
  </>
);

// Summary cards - use standardized component
export const SummaryCards = StandardSummaryCards;

// Enhanced data table using EnhancedTable component
export const DataTable = ({
  columns = [],
  rows = [],
  rowKey = (r) => r.id,
  renderActions,
  sortBy,
  sortOrder,
  onSort,
  emptyMessage,
  showPagination = true,
  defaultPageSize = 10,
  pageSizeOptions = [10, 25, 50, 100]
}) => {
  // Convert columns to EnhancedTable format
  const enhancedColumns = columns.map(col => ({
    field: col.field,
    headerName: col.headerName,
    type: col.type,
    align: col.align,
    sortable: col.sortable,
    renderCell: col.render
  }));

  return (
    <EnhancedTable
      columns={enhancedColumns}
      data={rows}
      defaultSortColumn={sortBy}
      defaultSortDirection={sortOrder?.toLowerCase()}
      showPagination={showPagination}
      defaultPageSize={defaultPageSize}
      pageSizeOptions={pageSizeOptions}
      emptyMessage={emptyMessage}
      actions={renderActions}
    />
  );
};

// Editor dialog wrapper to align titles/actions
export const EditorDialog = ({
  open,
  title,
  onClose,
  onSave,
  saveLabel = 'Save',
  saving = false,
  onSaveDraft,
  draftLabel = 'Save Draft',
  savingDraft = false,
  children,
  maxWidth = 'md',
  fullWidth = true,
  actions = null,
  error = null,
  fullScreenMobile = true
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      // disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={fullScreenMobile && isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile && fullScreenMobile ? 0 : '16px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>{error}</Alert>}
        {children}
      </DialogContent>
      <DialogActions sx={{
        p: 3,
        pt: 1,
        gap: 1.5,
        borderTop: isMobile ? `1px solid ${theme.palette.divider}` : 'none',
        position: isMobile && fullScreenMobile ? 'sticky' : 'static',
        bottom: 0,
        bgcolor: 'background.paper',
        zIndex: 1
      }}>
        {actions || (
          <>
            <Button onClick={onClose} disabled={saving || savingDraft} color="secondary" sx={{ fontWeight: 600 }}>Cancel</Button>
            <Box sx={{ flexGrow: 1 }} />
            {onSaveDraft && (
              <LoadingButton
                onClick={onSaveDraft}
                variant="outlined"
                color="primary"
                loading={savingDraft}
                loadingText="Saving Draft..."
                disabled={saving}
                sx={{ borderRadius: '8px', fontWeight: 700, px: 3 }}
              >
                {draftLabel}
              </LoadingButton>
            )}
            <LoadingButton
              onClick={onSave}
              variant="contained"
              color="primary"
              loading={saving}
              loadingText="Saving..."
              disabled={savingDraft}
              sx={{ borderRadius: '8px', fontWeight: 700, px: 4 }}
            >
              {saveLabel}
            </LoadingButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Form Section for grouping fields
export const FormSection = ({ title, children, sx = {} }) => (
  <Box sx={{ mb: 4, ...sx }}>
    {title && (
      <Typography variant="subtitle2" sx={{
        fontWeight: 800,
        color: 'secondary.main',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        '&:after': {
          content: '""',
          flex: 1,
          height: '1px',
          bgcolor: 'divider',
          ml: 2
        }
      }}>
        {title}
      </Typography>
    )}
    <Grid container spacing={2.5}>
      {children}
    </Grid>
  </Box>
);

// Standard Status Badge
export const StatusBadge = ({ status, color = 'default', sx = {} }) => {
  const getColors = () => {
    const s = status?.toLowerCase()?.replace(/_/g, ' ');
    if (['posted', 'paid', 'approved', 'active', 'success', 'completed'].includes(s)) return { color: 'success', label: 'Posted' };
    if (['pending_post', 'posting'].includes(status?.toLowerCase())) return { color: 'warning', label: 'Posting...' };
    if (['draft', 'pending', 'open', 'partial', 'partially paid'].includes(s)) return { color: 'warning', label: status };
    if (['reversed', 'cancelled', 'void', 'inactive', 'overdue', 'error', 'rejected'].includes(s)) return { color: 'error', label: status };
    return { color: 'default', label: status };
  };

  const config = getColors();

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      sx={{
        fontWeight: 700,
        borderRadius: '6px',
        textTransform: 'capitalize',
        ...sx
      }}
    />
  );
};

// Export all standardized components
export { PageHeader, RefreshButton, StandardTable, ExportButton, FormField };
const UnifiedUI = {
  ActionBar,
  FilterBar,
  SummaryCards,
  DataTable,
  EditorDialog,
  PageHeader,
  RefreshButton,
  StandardTable
};

export default UnifiedUI;
